import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

// ============================================================
// NAV HEADER — Morphing pill navbar para AgroPort.
//
// • Glass dark + pill verde animado que sigue al item activo
//   con framer-motion layoutId (efecto morphing tipo Apple/21st).
// • SIEMPRE visible: position fixed top siempre arriba del viewport.
// • Auto-detecta sección activa via IntersectionObserver (scroll-spy).
// • Logo izquierda, nav central, CTA "Iniciar Sesión" derecha.
// • Mobile: hamburguesa con sheet animado.
//
// Colores AgroPort: verde #02BF44, amarillo #FAD22C, dark #0A0F0B.
// ============================================================

const DEFAULT_NAV_ITEMS = [
  { id: "top",       label: "Inicio" },
  { id: "about",     label: "Nosotros" },
  { id: "productos", label: "Productos" },
  { id: "cobertura", label: "Cobertura" },
  { id: "galeria",   label: "Galería" },
  { id: "contacto",  label: "Contacto" },
];

export default function NavHeader({ onAccess, userLogged = false, items, ctaLabel, ctaLabelLogged }) {
  // Items del CMS (filtra deshabilitados), fallback a defaults
  const NAV_ITEMS = (Array.isArray(items) && items.length > 0)
    ? items.filter((i) => i.enabled !== false).map((i) => ({ id: i.id, label: i.label }))
    : DEFAULT_NAV_ITEMS;
  const cta = userLogged
    ? (ctaLabelLogged || "Ir al ERP")
    : (ctaLabel || "Iniciar Sesión");

  const [active, setActive] = useState(NAV_ITEMS[0]?.id || "top");
  const [hovered, setHovered] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Scroll spy: detecta qué sección está en viewport
  useEffect(() => {
    const targets = NAV_ITEMS
      .map(({ id }) => document.getElementById(id))
      .filter(Boolean);
    if (!targets.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        // Elige la entry más cercana al top con mayor visibilidad
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-25% 0px -55% 0px", threshold: [0, 0.15, 0.3, 0.6] },
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  // Glass background gana intensidad al hacer scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Cerrar mobile al cambiar sección
  useEffect(() => { setMobileOpen(false); }, [active]);

  const go = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else if (id === "top") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const highlightId = hovered ?? active;

  return (
    <>
      <header
        data-testid="landing-navbar"
        className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none"
        style={{ paddingTop: scrolled ? 12 : 20 }}
      >
        <div
          className={[
            "pointer-events-auto flex items-center gap-2 md:gap-3 px-2 md:px-3 py-2",
            "rounded-full border transition-all duration-500",
            scrolled
              ? "bg-[#0A0F0B]/80 border-white/10 shadow-[0_8px_30px_-6px_rgba(0,0,0,0.6)] backdrop-blur-2xl"
              : "bg-[#0A0F0B]/55 border-white/[0.08] backdrop-blur-xl",
          ].join(" ")}
        >
          {/* LOGO */}
          <button
            onClick={() => go("top")}
            data-testid="landing-logo"
            className="flex items-center gap-2 pl-2 pr-1 flex-shrink-0"
            aria-label="Ir al inicio"
          >
            <img
              src="/agroport-logo-green.png"
              alt="AGROPORT"
              className="h-8 md:h-9 w-auto"
            />
          </button>

          {/* DIVIDER */}
          <span className="hidden lg:block w-px h-6 bg-white/10" />

          {/* DESKTOP NAV — morphing pill */}
          <nav
            className="hidden lg:flex items-center relative"
            onMouseLeave={() => setHovered(null)}
            data-testid="navbar-desktop-nav"
          >
            {NAV_ITEMS.map((item) => {
              const isHighlight = item.id === highlightId;
              return (
                <button
                  key={item.id}
                  onClick={() => go(item.id)}
                  onMouseEnter={() => setHovered(item.id)}
                  data-testid={`nav-item-${item.id}`}
                  data-active={item.id === active}
                  className="relative px-4 xl:px-5 py-2 text-[13px] font-semibold tracking-wide rounded-full"
                >
                  {isHighlight && (
                    <motion.span
                      layoutId="nav-pill"
                      className="absolute inset-0 rounded-full bg-agro-green shadow-[0_4px_18px_-2px_rgba(2,191,68,0.55)]"
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    />
                  )}
                  <span
                    className={[
                      "relative z-10 transition-colors duration-200",
                      isHighlight ? "text-black" : "text-white/75 hover:text-white",
                    ].join(" ")}
                  >
                    {item.label}
                  </span>
                </button>
              );
            })}
          </nav>

          {/* CTA + MOBILE TOGGLE */}
          <div className="flex items-center gap-1 md:gap-2 pl-1 md:pl-2">
            <span className="hidden lg:block w-px h-6 bg-white/10 mr-1" />
            <button
              onClick={onAccess}
              data-testid="navbar-login-btn"
              className="group hidden sm:inline-flex items-center gap-1.5 bg-white text-black font-semibold rounded-full pl-4 pr-1.5 py-1.5 text-[12px] hover:bg-agro-yellow transition-colors"
            >
              <span>{cta}</span>
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-black text-white group-hover:bg-agro-green group-hover:text-black transition-colors">
                <ArrowRight size={13} strokeWidth={2.6} />
              </span>
            </button>

            <button
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menú"
              data-testid="navbar-mobile-toggle"
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full text-white hover:bg-white/10"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE SHEET */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22 }}
            className="fixed top-[72px] left-3 right-3 z-40 lg:hidden rounded-3xl bg-[#0A0F0B]/95 backdrop-blur-2xl border border-white/10 p-3 shadow-2xl"
            data-testid="navbar-mobile-sheet"
          >
            <div className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => {
                const isActive = item.id === active;
                return (
                  <button
                    key={item.id}
                    onClick={() => go(item.id)}
                    data-testid={`nav-mobile-${item.id}`}
                    className={[
                      "text-left px-4 py-3 rounded-2xl text-sm font-semibold transition-colors",
                      isActive
                        ? "bg-agro-green text-black"
                        : "text-white/80 hover:bg-white/5",
                    ].join(" ")}
                  >
                    {item.label}
                  </button>
                );
              })}
              <div className="h-px bg-white/10 my-2" />
              <button
                onClick={() => { setMobileOpen(false); onAccess(); }}
                className="flex items-center justify-between px-4 py-3 rounded-2xl bg-white text-black text-sm font-semibold"
              >
                <span>{cta}</span>
                <ArrowRight size={16} strokeWidth={2.6} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
